/**
 * WatchVaultApprovalSheet
 *
 * Shown on the iPhone when the Apple Watch requests a vault QR code (or 2FA
 * code) and the VEK is not already in the in-memory session cache. The user
 * must:
 *   1. Tap "Approve" on this sheet, AND
 *   2. Pass a fresh biometric (Face ID / Touch ID) check.
 *
 * Tapping "Deny" — or failing / cancelling biometrics — tells the watch the
 * request was rejected.
 *
 * Security model: the watch is a less-trusted surface (lost wrist, glance
 * shoulder-surfing, etc.), so we re-verify the human at every cold approval
 * regardless of whether the phone is currently unlocked. SecureStore's own
 * biometric guard sits behind this and may also prompt when loading the VEK,
 * but iOS will typically coalesce the two prompts because they happen back-
 * to-back inside the same authentication context.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import * as Notifications from 'expo-notifications';
import * as LocalAuthentication from 'expo-local-authentication';
import type { IconPrefix, IconName } from '@fortawesome/fontawesome-svg-core';
import type { WatchVaultApprovalRequest } from '../hooks/useWatchSync';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import debug from '../utilities/debug';
import Icon from './Icon';

interface Props {
  request: WatchVaultApprovalRequest | null;
  onClose: () => void;
}

const CRED_ICON: Record<string, [IconPrefix, IconName]> = {
  login:     ['fad', 'lock'],
  card:      ['fad', 'credit-card'],
  pin:       ['fad', 'shield-keyhole'],
  insurance: ['fad', 'shield-heart'],
  identity:  ['fad', 'id-card'],
  bank:      ['fad', 'landmark-flag'],
  note:      ['fad', 'file-shield'],
  wifi:      ['fas', 'wifi'],
  medical:   ['fad', 'message-medical'],
};

const WatchVaultApprovalSheet: React.FC<Props> = ({ request, onClose }) => {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 440 });
  const [approving, setApproving] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // When the sheet opens, dismiss the corresponding local notification (if any)
  // so it doesn't linger in Notification Center after the user is already handling it.
  useEffect(() => {
    if (!request) return;
    setAuthError(null);
    Notifications.dismissNotificationAsync(`vault_qr_approval_${request.credentialId}`).catch(() => {});
  }, [request]);

  if (!request) return null;

  const iconDef = CRED_ICON[request.credentialType] ?? CRED_ICON.login;

  const handleApprove = async () => {
    setAuthError(null);
    setApproving(true);
    try {
      // Gate: require a fresh biometric check before we even ask the
      // approve callback to load the VEK. We allow the system passcode
      // fallback (`disableDeviceFallback: false`) so users with a broken
      // sensor / sweaty fingers / sunglasses still have a path through.
      // If hardware/biometrics aren't available on this device we skip the
      // gate rather than locking the user out of their own watch.
      const hasHardware = await LocalAuthentication.hasHardwareAsync().catch(() => false);
      const isEnrolled = hasHardware
        ? await LocalAuthentication.isEnrolledAsync().catch(() => false)
        : false;

      if (hasHardware && isEnrolled) {
        const auth = await LocalAuthentication.authenticateAsync({
          promptMessage: 'Approve watch vault request',
          fallbackLabel: 'Use passcode',
          disableDeviceFallback: false,
          cancelLabel: 'Cancel',
        });
        if (!auth.success) {
          debug.info('ui', 'Vault approval cancelled at biometric gate', {
            credentialId: request.credentialId,
            reason: 'error' in auth ? auth.error : 'unknown',
          });
          setAuthError(
            'error' in auth && auth.error === 'user_cancel'
              ? null
              : 'Authentication failed. Try again.',
          );
          return;
        }
      }

      const ok = await request.approve();
      if (ok) {
        onClose();
      } else {
        setAuthError('Could not unlock the vault. Try again.');
      }
    } finally {
      setApproving(false);
    }
  };

  const handleDeny = () => {
    request.deny();
    onClose();
  };

  return (
    <Modal
      transparent
      visible={!!request}
      animationType={sheet.animationType}
      onRequestClose={handleDeny}
    >
      <View style={[styles.overlay, sheet.overlayStyle]}>
        <View style={[styles.sheet, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }, sheet.sheetStyle, sheet.desktopContentStyle]}>
          {/* Watch icon badge */}
          <View style={[styles.watchBadge, { backgroundColor: theme.colors.surface }]}>
            <Icon icon={['fad', 'watch']} size={18} color={theme.colors.primary} />
          </View>

          <Text style={[styles.heading, { color: theme.colors.text }]}>
            Watch Vault Request
          </Text>
          <Text style={[styles.subheading, { color: theme.colors.muted }]}>
            Your Apple Watch is requesting access to a credential.
          </Text>

          {/* Credential identity card */}
          <View style={[styles.credCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <View style={[styles.credIconCircle, { backgroundColor: theme.colors.card }]}>
              <Icon icon={iconDef} size={20} color={theme.colors.primary} />
            </View>
            <View style={styles.credText}>
              <Text style={[styles.credLabel, { color: theme.colors.text }]} numberOfLines={1}>
                {request.label}
              </Text>
              <Text style={[styles.credType, { color: theme.colors.muted }]}>
                {request.credentialType.replace(/_/g, ' ')}
              </Text>
            </View>
          </View>

          <Text style={[styles.hint, { color: theme.colors.muted }]}>
            Approving requires Face ID. The unlock is good for one credential
            and expires in 5 minutes.
          </Text>

          {authError && (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>
              {authError}
            </Text>
          )}

          {approving ? (
            <ActivityIndicator size="small" color={theme.colors.primary} style={styles.spinner} />
          ) : (
            <View style={styles.buttons}>
              <TouchableOpacity
                style={[styles.btn, styles.denyBtn, { borderColor: theme.colors.border }]}
                onPress={handleDeny}
                activeOpacity={0.7}
              >
                <Text style={[styles.denyText, { color: theme.colors.muted }]}>Deny</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.btn, styles.approveBtn, { backgroundColor: theme.colors.primary }]}
                onPress={handleApprove}
                activeOpacity={0.7}
              >
                <Text style={[styles.approveText, { color: theme.colors.primaryText }]}>
                  Approve
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

export default WatchVaultApprovalSheet;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  sheet: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    borderBottomWidth: 0,
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 40,
    alignItems: 'center',
  },
  watchBadge: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  heading: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 6,
    textAlign: 'center',
  },
  subheading: {
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 18,
  },
  credCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    width: '100%',
    marginBottom: 16,
  },
  credIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  credText: {
    flex: 1,
  },
  credLabel: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  credType: {
    fontSize: 12,
    textTransform: 'capitalize',
  },
  hint: {
    fontSize: 11,
    textAlign: 'center',
    marginBottom: 14,
    lineHeight: 16,
    paddingHorizontal: 8,
  },
  errorText: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 12,
    paddingHorizontal: 8,
  },
  spinner: {
    marginVertical: 16,
  },
  buttons: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
  },
  btn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  denyBtn: {
    borderWidth: 1,
  },
  approveBtn: {},
  denyText: {
    fontSize: 15,
    fontWeight: '500',
  },
  approveText: {
    fontSize: 15,
    fontWeight: '600',
  },
});
